import React, { useState, useEffect, useContext } from "react";
import {
  Users,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Tag,
} from "lucide-react";
import toast from "react-hot-toast";
import API from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBtnBack,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const StudentApproval = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [students, setStudents] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [selectedReferralCode, setSelectedReferralCode] = useState("");
  const [loading, setLoading] = useState(true);

  // --- CONTROL STATES ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const isMainAdmin =
    String(currentUser?.email || "").toLowerCase() === "medhashala@gmail.com" ||
    String(currentUser?.role || "").toLowerCase() === "admin";

// FETCH STUDENTS & ACTIVE REFERRAL CODES
  const fetchData = async () => {
    setLoading(true);
    try {
      const studentEndpoint = isMainAdmin 
        ? "/auth/admin/student-requests" 
        : "/student/institution-students";

      // Call the direct backend endpoint we created
      const [studentRes, refRes] = await Promise.all([
        API.get(studentEndpoint),
        API.get("/student/active-referral-codes").catch(() => ({ data: { data: [] } })),
      ]);

      let allStudents = Array.isArray(studentRes.data?.data) ? studentRes.data.data : [];
      const activeRefs = Array.isArray(refRes.data?.data) ? refRes.data.data : [];

      if (!isMainAdmin && currentUser?.tenantKey) {
        allStudents = allStudents.filter(
          (st) =>
            String(st.tenantKey || "").toUpperCase() ===
            String(currentUser.tenantKey).toUpperCase()
        );
      }

      setStudents(allStudents);
      setReferralCodes(activeRefs);

    } catch (err) {
      console.error("Fetch Error:", err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  // UPDATE STATUS (ACCEPT / REJECT)
  const handleStatusChange = async (id, status) => {
    try {
      await API.patch(`/student/student-approval/${id}`, { status });
      toast.success(`STUDENT ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "ACTION FAILED");
    }
  };

  // DELETE STUDENT
  const handleDelete = async (id, email) => {
    if (email?.toLowerCase() === "medhashala@gmail.com") {
      toast.error("MAIN ADMIN ACCOUNT CANNOT BE DELETED");
      return;
    }
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS STUDENT?")) {
      try {
        await API.delete(`/student/student/${id}`);
        toast.success("STUDENT DELETED SUCCESSFULLY");
        fetchData();
      } catch (err) {
        console.error("Delete Error:", err);
        toast.error("DELETE FAILED");
      }
    }
  };

  // --- FILTERING ---
  const filteredData = students.filter((item) => {
    // 1. Referral Code Filter Match
    if (selectedReferralCode && String(item.referredBy || "").trim() !== String(selectedReferralCode).trim()) {
      return false;
    }

    // 2. Search Term Filter Match
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();

    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };

    return (
      startsWithFirstLetter(item.name) ||
      startsWithFirstLetter(item.userId) ||
      startsWithFirstLetter(item.email) ||
      startsWithFirstLetter(item.phone) ||
      startsWithFirstLetter(item.adminApprovalStatus)
    );
  });

  // --- PAGINATION SLICING ---
  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar relative`}>
      {/* HEADER CARD */}
      <div className={masterPanelCard}>
        <div className={masterHeaderContent}>
          <div className={masterHeaderBrand}>
            <div className={masterHeaderIconBox}>
              <Users size={13} />
            </div>
            <h1 className={masterHeadingMain}>Student Registration Approvals</h1>
          </div>

          {/* HEADER ACTIONS: Referral Filter on the Left Side of SYNC */}
          <div className={masterHeaderActions}>
            <div className="flex items-center gap-1.5">
              <Tag size={13} className="text-[#0E3652]" />
              <select
                value={selectedReferralCode}
                onChange={(e) => {
                  setSelectedReferralCode(e.target.value);
                  setCurrentPage(1);
                }}
                className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-xs focus:outline-none focus:ring-1 focus:ring-[#0E3652]"
              >
                <option value="">All Referral Codes</option>
                {referralCodes.map((refItem) => {
                  const codeVal = refItem.referralCode || refItem.code;
                  return (
                    <option key={refItem._id || codeVal} value={codeVal}>
                      {codeVal}
                    </option>
                  );
                })}
              </select>
              {selectedReferralCode && (
                <button
                  type="button"
                  onClick={() => setSelectedReferralCode("")}
                  className="text-[10px] font-bold text-blue-600 hover:underline px-1"
                >
                  Clear
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={fetchData}
              className={masterBtnBack}
              title="Refresh Records"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              <span>SYNC</span>
            </button>
          </div>
        </div>
      </div>

      {/* COMBINED SEARCH & PAGINATION BAR */}
      <PaginationSearchbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="SEARCH NAME, ID, EMAIL, OR STATUS BY FIRST LETTER..."
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalItems={filteredData.length}
      />

      {/* TABLE */}
      <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
        <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
          <table className={masterTable}>
            <thead>
              <tr className={masterTableHead}>
                <th className={masterThCode}>SL NO</th>
                <th className={masterThName}>STUDENT NAME</th>
                <th className={masterThName}>EMAIL</th>
                <th className={masterThName}>REFERAL CODE</th>
                <th className={masterThName}>MOBILE NO.</th>
                <th className={masterThStatus}>STATUS</th>
                <th className={masterThManage}>MANAGE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className={masterTdEmpty}>
                    Syncing Database...
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const isApproved = item.adminApprovalStatus === "APPROVED";
                  const isRejected = item.adminApprovalStatus === "REJECTED";
                  const isMainAdminAccount = item.email?.toLowerCase() === "medhashala@gmail.com";

                  return (
                    <tr key={item._id} className={masterTableRow}>
                      <td className={masterTdCode}>{startIndex + index + 1}</td>
                      <td className={masterTdName}>
                        <div className="font-semibold">{item.name}</div>
                      </td>
                      <td className={masterTdName}>
                        <div>{item.email}</div>
                      </td>
                      <td className={masterTdName}>
                        <span className="font-mono text-xs">{item.referredBy || "N/A"}</span>
                      </td>
                      <td className={masterTdName}>
                        <div>{item.phone || "N/A"}</div>
                      </td>
                      <td className={masterTdStatus}>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            isApproved
                              ? "bg-emerald-100 text-emerald-800"
                              : isRejected
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {item.adminApprovalStatus || "PENDING"}
                        </span>
                      </td>
                      <td className={masterTdManage}>
                        <div className={masterTdManageFlex}>
                          {isMainAdminAccount ? (
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                              Protected Admin
                            </span>
                          ) : (
                            <>
                              {/* Accept Button */}
                              <button
                                type="button"
                                onClick={() => handleStatusChange(item._id, "APPROVED")}
                                disabled={isApproved}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${
                                  isApproved
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                                }`}
                                title="Accept"
                              >
                                <CheckCircle size={12} /> Join
                              </button>

                              {/* Reject Button */}
                              <button
                                type="button"
                                onClick={() => handleStatusChange(item._id, "REJECTED")}
                                disabled={isRejected}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${
                                  isRejected
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                    : "bg-amber-600 text-white hover:bg-amber-700 shadow-sm"
                                }`}
                                title="Reject"
                              >
                                <XCircle size={12} /> Suspend
                              </button>

                             {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => handleDelete(item._id, item.email)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className={masterTdEmpty}>
                    No Student Records Found For This Selection
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentApproval;